import React, { useEffect, useMemo, Suspense, lazy } from 'react';
import { StyleSheetManager } from 'styled-components';
import { Switch } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import { Helmet } from 'react-helmet';

import PublicLayout, { ApplicationFormLayout, PopupLayout, LoginLayout } from './layouts/PublicLayout';
import DefaultLayout from 'src/layouts/DefaultLayout';
import PhonicsLayout from 'src/layouts/PhonicsLayout';
import AICoachLayout from 'src/layouts/AICoachLayout';
import AICoachNewLayout from 'src/layouts/AICoachNewLayout';
import AITalkingTutorLayout from 'src/layouts/AITalkingTutorLayout';
import ToolLayout from 'src/layouts/ToolLayout';
import DDSCLayout from 'src/layouts/DDSCLayout';
import LevelUpLayout from 'src/layouts/LevelUpLayout';
import LevelUpLayout2 from 'src/layouts/LevelUpLayout2';
import LevelUpLayout3 from 'src/layouts/LevelUpLayout3';
import AirPlusLayout from 'src/layouts/AirPlusLayout';

import { useDispatch, useSelector } from 'react-redux';
import { setJsonData } from 'src/operations/store/jsonDataSlice';
import MaintenanceNotice from 'src/components/MaintenanceNotice';
import 'moment/locale/ko';
import { getCurrentToken } from 'src/utils';
import { useAiSpeakingAudioUploadProcessor } from 'src/utils/aiSpeakingAudioUploadQueue';
import koKR from 'antd/lib/locale/ko_KR';
import enUS from 'antd/lib/locale/en_US';
import { classStoreData } from 'src/operations/store';
import { useLazyQuery } from '@apollo/client';
import { USER_ME } from 'src/operations/queries/getUser';
import * as Sentry from '@sentry/react';
import moment from 'moment';
// Lazy loaded route components (code-splitting)
const EngEduNotice = lazy(() => import('./pages/engedunotice'));


const Administrator = lazy(() => import('./routes/Administrator'));
const HeadManager = lazy(() => import('./routes/HeadManager'));
const BranchManager = lazy(() => import('./routes/BranchManager'));
const CampusManager = lazy(() => import('./routes/CampusManager'));
const Teacher = lazy(() => import('./routes/Teacher'));
const Student = lazy(() => import('./routes/Student'));
const Login = lazy(() => import('./pages/Login/index.js'));
const Login2 = lazy(() => import('./pages/Login/index2.js'));
const LevelTest = lazy(() => import('./pages/LevelTest'));
const LevelTestNew = lazy(() => import('./pages/LevelTest/indexNew'));
const LevelTestMain = lazy(() => import('./pages/LevelTest/LevelTestMain'));
const EvineLevelTestAppForm = lazy(() => import('./popups/EvineLevelTestAppForm'));
const PturnLevelTestAppForm = lazy(() => import('./popups/PturnLevelTestAppForm'));
const LevelTestAppForm = lazy(() => import('./popups/LevelTestAppForm'));
const OnlineStudyResult = lazy(() => import('./popups/OnlineStudyResult'));
const ElibraryStudyResult = lazy(() => import('./popups/ElibraryStudyResult'));
const PayLetterCancel = lazy(() => import('./popups/PayLetterCancel'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Policy = lazy(() => import('./pages/Policy'));
const MetaUserInfo = lazy(() => import('./pages/MetaExam/MetaUserInfo'));
const UserInfoNew = lazy(() => import('./pages/MetaExam/UserInfoNew'));
const Survey = lazy(() => import('./pages/MetaExam/Survey'));
const MetaLogin = lazy(() => import('./pages/MetaExam/MetaLogin'));
const MetaExamType = lazy(() => import('./pages/MetaExam/MetaExamType'));
const ReadingInfo = lazy(() => import('./pages/MetaExam/ReadingInfo'));
const ReadingTest = lazy(() => import('./pages/MetaExam/ReadingTest'));
const MetaFinished = lazy(() => import('./pages/MetaExam/MetaFinished'));
const MetaSampleQuestion = lazy(() => import('./pages/MetaExam/MetaSampleQuestion'));
const ImageTest = lazy(() => import('./pages/MetaExam/ImageTest'));
const MetaReport = lazy(() => import('./pages/MetaExam/MetaReport'));
const MetaReportAI = lazy(() => import('./pages/MetaExam/MetaReportAI'));
const MetaTestStart = lazy(() => import('./pages/MetaExam/MetaTestStart'));
const InstructionalGuideMeta = lazy(() => import('./pages/MetaExam/InstructionalGuideMeta'));
const InstructionalMetaExamType = lazy(() => import('./pages/MetaExam/InstructionalMetaExamType'));
const VocaTestSample = lazy(() => import('./pages/MetaExam/VocaTestSample'));
const ReadingTestSample = lazy(() => import('./pages/MetaExam/ReadingTestSample'));
const ListeningTestSample = lazy(() => import('./pages/MetaExam/ListeningTestSample'));
const ExternalTool = lazy(() => import('./pages/ExternalTool'));
const BookrTool = lazy(() => import('./pages/Bookr/Recording'));
const BookrRedirect = lazy(() => import('src/pages/Bookr/Redirect'));
const BlankAppPage = lazy(() => import('src/pages/BlankAppPage'));
const Cmaker = lazy(() => import('./pages/Cmaker'));
const FAQ = lazy(() => import('./components/common/FAQ'));
const FAQEvine = lazy(() => import('./components/common/FAQ/Evine'));
const AiEssayCorrection = lazy(() => import('./pages/LevelUp/BookReport'));
const VocaTool = lazy(() => import('./pages/VocaTool'));
const VocaCheck = lazy(() => import('./pages/VocaTool/VocaCheck'));
const VocaFullWidth = lazy(() => import('./pages/VocaTool/VocaFullWidth'));
const VocaDictation = lazy(() => import('./pages/VocaTool/VocaDictation'));
const VocaFlashcard = lazy(() => import('./pages/VocaTool/VocaFlashcard'));
const VocaFlashCardTyping = lazy(() => import('./pages/VocaTool/VocaFlashCardTyping'));
const VocaUnscramble = lazy(() => import('./pages/VocaTool/VocaUnscramble'));
const VocaQuizFrame = lazy(() => import('./pages/VocaTool/VocaQuizFrame'));
const LevelUpPortfolio = lazy(() => import('./pages/LevelUp/Library/Portfolio'));
const LevelUpPortfolio2 = lazy(() => import('./pages/LevelUp/Library/Portfolio2'));
const LevelUpPortfolio3 = lazy(() => import('./pages/LevelUp/Library/Portfolio3'));
const LevelUpFavorite = lazy(() => import('./pages/LevelUp/Library/Favorite'));
const LevelUpFavorite2 = lazy(() => import('./pages/LevelUp/Library/Favorite2'));
const LevelUpFavorite3 = lazy(() => import('./pages/LevelUp/Library/Favorite3'));
const LevelUpInProgress = lazy(() => import('./pages/LevelUp/Library/InProgress'));
const LevelUpInProgress2 = lazy(() => import('./pages/LevelUp/Library/InProgress2'));
const LevelUpInProgress3 = lazy(() => import('./pages/LevelUp/Library/InProgress3'));
const LevelUpPointsAndCalendarPage = lazy(() => import('./pages/LevelUp/Library/PtsAndCalendar'));
const LevelUpPointsAndCalendarPage2 = lazy(() => import('./pages/LevelUp/Library/PtsAndCalendar2'));
const LevelUpPointsAndCalendarPage3 = lazy(() => import('./pages/LevelUp/Library/PtsAndCalendar3'));
const LevelUpHome = lazy(() => import('./pages/LevelUp'));
const LevelUpHome2 = lazy(() => import('./pages/LevelUp/levelup'));
const LevelUpHome3 = lazy(() => import('./pages/LevelUp/levelup2'));
const AirPlusHome = lazy(() => import('./pages/AirPlus/AirPlusHome'));
const AirPlusLibrary = lazy(() => import('./pages/AirPlus/Library'));
const AirPlusSearch = lazy(() => import('./pages/AirPlus/Search'));
const AirPlusPortfolio = lazy(() => import('./pages/AirPlus/Portfolio'));
const AirPlusMyPage = lazy(() => import('./pages/AirPlus/MyPage'));
const AirPlusBookReportList = lazy(() => import('./pages/AirPlus/BookReportList'));
const AirPlusBookReport = lazy(() => import('./pages/AirPlus/BookReport'));
const AirPlusBookReportGenerate = lazy(() => import('./pages/AirPlus/BookReport/ReportGenerate'));
const AirPlusReportDetailsByDate = lazy(() => import('./pages/AirPlus/Report/DetailsByDate'));
const AirPlusReportRubric = lazy(() => import('./pages/AirPlus/Report/Rubric'));
const AirPlusLevelTest = lazy(() => import('./pages/AirPlus/LevelTest'));
const AirPlusAssignResult = lazy(() => import('./pages/AirPlus/Results/AssignResult'));
const LevelUpLevelTest = lazy(() => import('./pages/LevelUp/LevelTest'));
const AirPlusBookStudyPage = lazy(() => import('./pages/AirPlus/Study'));
const AirPlusFluency = lazy(() => import('./pages/AirPlus/Fluency'));
const AirPlusRecording = lazy(() => import('./pages/AirPlus/Recording'));
const LevelUpLibrary = lazy(() => import('./pages/LevelUp/Library'));
const LevelUpLibrary3 = lazy(() => import('./pages/LevelUp/Library/index2'));
const LevelUpSearch = lazy(() => import('./pages/LevelUp/Library/Search'));
const LevelUpSearch2 = lazy(() => import('./pages/LevelUp/Library/Search2'));
const LevelUpSearch3 = lazy(() => import('./pages/LevelUp/Library/Search3'));
const LevelUpManage = lazy(() => import('src/pages/LevelUp/Report'));
const LevelUpDetailsByDate = lazy(() => import('src/pages/LevelUp/Report/detailsByDate'));
const LevelUpBookReportStudy = lazy(() => import('src/pages/LevelUp/Report/study'));
const LevelUpVocaCard = lazy(() => import('src/pages/LevelUp/Library/Study/Voca/Flashcard/index2'));
const LevelUpVocaType = lazy(() => import('src/pages/LevelUp/Library/Study/Voca/Type'));
const LevelUpVocaMatch = lazy(() => import('src/pages/LevelUp/Library/Study/Voca/Match'));
const LevelUpVocaQuiz = lazy(() => import('src/pages/LevelUp/Library/Study/Voca/Quiz'));
const LevelUpSentenceCard = lazy(() => import('src/pages/LevelUp/Library/Study/Sentence/Flashcard'));
const LevelUpSentenceRepeat = lazy(() => import('src/pages/LevelUp/Library/Study/Sentence/Repeat'));
const LevelUpSentenceDnD = lazy(() => import('src/pages/LevelUp/Library/Study/Sentence/DnD'));
const LevelUpSentenceDictation = lazy(() => import('src/pages/LevelUp/Library/Study/Sentence/Dictation'));
const LevelUpQuiz1 = lazy(() => import('src/pages/LevelUp/Quiz/Quiz1'));
const LevelUpQuiz2 = lazy(() => import('src/pages/LevelUp/Quiz/Quiz2'));
const LevelUpQuiz3 = lazy(() => import('src/pages/LevelUp/Quiz/Quiz3'));
const LevelUpBookStudyPage = lazy(() => import('src/pages/LevelUp/Library/Study/index'));
const LevelUpReportandWPMResultPage = lazy(() => import('./pages/LevelUp/Results/ReportandWPM'));
const LevelUpWPM = lazy(() => import('./pages/LevelUp/WPM'));
const LevelUpWPMResult = lazy(() => import('./pages/LevelUp/WPM/Result'));
const CardMatchingGame = lazy(() => import('./pages/LevelUp/Game/CardMatch'));
const CrossPuzzleGame = lazy(() => import('./pages/LevelUp/Game/CrossWord/index'));
const WordFallGame = lazy(() => import('./pages/LevelUp/Game/WordFall'));
const AirPlusBookDescriptionPage = lazy(() => import('src/pages/AirPlus/BookDescription'));
const AirPlusReader = lazy(() => import('src/pages/AirPlus/Reader'));
const AirPlusEpubReader = lazy(() => import('src/pages/AirPlus/Reader/EpubReader'));
const AirPlusCrossPuzzleGame = lazy(() => import('src/pages/AirPlus/Game/CrossWord'));
const AirPlusWordFallGame = lazy(() => import('src/pages/AirPlus/Game/WordFall'));
const AirPlusCardMatchGame = lazy(() => import('src/pages/AirPlus/Game/CardMatch'));
const AirPlusVocaCard = lazy(() => import('src/pages/AirPlus/Library/Study/Voca/Flashcard/index2'));
const AirPlusVocaType = lazy(() => import('src/pages/AirPlus/Library/Study/Voca/Type'));
const AirPlusVocaMatch = lazy(() => import('src/pages/AirPlus/Library/Study/Voca/Match'));
const AirPlusVocaQuiz = lazy(() => import('src/pages/AirPlus/Library/Study/Voca/Quiz'));
const AirPlusWordMatchGame = lazy(() => import('./pages/AirPlus/Game/WordMatchMemory'));
const RubricPage = lazy(() => import('./pages/LevelUp/Results/Rubric'));
const RubricAiCoachPage = lazy(() => import('src/pages/AiCoach/Result/RubricAiCoach'));
const RubricAIWritingPage = lazy(() => import('src/pages/AIWriting/Result/RubricAiCoach'));
const BookDescriptionPage = lazy(() => import('src/pages/LevelUp/BookDescription'));
const LevelUpReportGenerate = lazy(() => import('./pages/LevelUp/Report/generate'));
const LevelUpReader = lazy(() => import('./pages/LevelUp/Reader'));
const LevelUpTool = lazy(() => import('./pages/LevelUp/Recording'));
const EpubReader = lazy(() => import('./pages/LevelUp/Reader/EpubReader'));
const JoshuaTestResult = lazy(() => import('./pages/Learning/JoshuaTestResult'));
const PhonicsMonsterStageList = lazy(() => import('./pages/PhonicsMonster/stagelist'));
const PhonicsMonsterStage = lazy(() => import('./pages/PhonicsMonster/stage'));
const AiWritingCoach = lazy(() => import('./pages/AiCoach/Writing'));
const AIWritingCoachWhale = lazy(() => import('./pages/AIWriting/Writing'));
const AICoachReportGenerate = lazy(() => import('./pages/AiCoach/Generate'));
const AIWritingReportGenerate = lazy(() => import('./pages/AIWriting/Generate'));
const AITalkingTutorReportGenerate = lazy(() => import('./pages/AITalkingTutor/generate'));
const MonthlyReport = lazy(() => import('./pages/MonthlyReport'));
const AiTalkingStudy = lazy(() => import('./pages/AITalkingTutor/Writing'));
const AiTalkingStudyReal = lazy(() => import('./pages/AITalkingTutor/Speaking/Stage'));
const StudentAIHomeworkAssignment = lazy(() => import('./pages/AIHomework/Student/Assignment'));
const StudentAIHomeworkResult = lazy(() => import('./pages/AIHomework/Student/Result'));
const StarWordsPortfolio = lazy(() => import('./pages/StarWords/Portfolio'));
const StudentReportLevelTestEduco = lazy(() => import('./pages/Consulting/StudentReportLevelTestEduco'));
const StudentReportLevelTestEducoElementary = lazy(() => import('./pages/Consulting/StudentReportLevelTestEducoElementary'));
const StudentReportLevelTestJoshua = lazy(() => import('./pages/Consulting/StudentReportLevelTestJoshua'));
const StudentReportLevelTestWEnglish = lazy(() => import('./pages/Consulting/StudentReportLevelTestWEnglish'));
const NoAccess = lazy(() => import('./pages/NoAccess'));
const DashBoard = lazy(() => import('src/pages/DashBoard'));
const AirPlusDashBoard = lazy(() => import('src/pages/DashBoard/airplusDashboard'));
const OrderCheck = lazy(() => import('./pages/BookStore/OrderCheck'));
const DDSC = lazy(() => import('./pages/ddsc'));
const DDSCRoadmap = lazy(() => import('./pages/ddsc/roadmap'));
const DDSCVoca = lazy(() => import('./pages/ddsc/voca'));
const DDSCQuiz = lazy(() => import('./pages/ddsc/quiz'));
const DDSCTutor = lazy(() => import('./pages/ddsc/tutor'));
const DDSCBattle = lazy(() => import('./pages/ddsc/battle'));

const getAppTitle = (subdomain) => {
  switch (subdomain) {
    case 'rise':
    case 'e-rise':
      return 'I-QSD';
    case 'epublic':
      return 'A*Class';
    default:
      return 'Class Boom';
  }
};

const getFavoriteIcon = (subdomain) => {
  switch (subdomain) {
    case 'rise':
    case 'e-rise':
      return 'favicon.png';
    case 'epublic':
      return 'favicon_aclass.png';
    default:
      return 'logo192.png';
  }
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchJsonWithRetry = async (url, retries = 2, retryDelayMs = 500) => {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} for ${url}`);
      }
      return await response.json();
    } catch (error) {
      lastError = error;
      if (attempt < retries) {
        await wait(retryDelayMs * (attempt + 1));
      }
    }
  }
  throw lastError;
};

function App() {
  const defaultClass = useSelector(classStoreData);
  const dispatch = useDispatch();
  const jsonDataLoaded = useSelector((state) => state.jsonData.loaded);

  const subdomain = useMemo(() => {
    if (window.location.hostname) {
      return window.location.hostname.split('.')[0];
    }
    return 'default';
  }, []);

  const locales = (company) => {
    switch (company) {
      case 'creo':
        return enUS;
      default:
        return koKR;
    }
  };
  const [getUserData, { data: userMeData }] = useLazyQuery(USER_ME);
  const token = getCurrentToken();
  useAiSpeakingAudioUploadProcessor(userMeData?.me?.idx);
  useEffect(() => {
    if (token && token !== null) {
      getUserData({
        variables: { token: token },
      });
    }
  }, [token, getUserData]);

  useEffect(() => {
    if (userMeData) {
      Sentry.setUser({ id: userMeData?.me?.id, email: userMeData?.me?.email });
    } else if (!token) {
      Sentry.setUser(null);
    }
  }, [userMeData, token, dispatch]);

  useEffect(() => {
    // Load all JSON files at app start and set in Redux
    if (!jsonDataLoaded) {
      (async () => {
        try {
          const [guideVideos, guideVideosForAfterSchool, score, levelTestList, singleTestResult, testFiles, test, guidePPT] =
            await Promise.all([
              fetchJsonWithRetry('/guideVideos.json'),
              fetchJsonWithRetry('/guideVideosForAfterSchool.json'),
              fetchJsonWithRetry('/score.json'),
              fetchJsonWithRetry('/levelTestList.json'),
              fetchJsonWithRetry('/singleTestResult.json'),
              fetchJsonWithRetry('/testFiles.json'),
              fetchJsonWithRetry('/test.json'),
              fetchJsonWithRetry('/guidePPT.json'),
            ]);

          dispatch(
            setJsonData({
              guideVideos,
              guideVideosForAfterSchool,
              score,
              levelTestList,
              singleTestResult,
              testFiles,
              test,
              guidePPT,
            }),
          );
        } catch (_error) {
          // Silently ignore transient bootstrap JSON fetch errors.
          // The loader will retry again on the next mount because jsonDataLoaded remains false.
        }
      })();
    }
  }, [jsonDataLoaded, dispatch]);

  useEffect(() => {
    let maincontentloader = document.getElementById('maincontentloader');
    if (maincontentloader) {
      maincontentloader.style.display = 'none';
    }
  }, []);

  const isEngEduRestricted =
    defaultClass === 'engedu' &&
    moment().isSameOrAfter(moment('2026-10-27 10:00:00')) &&
    userMeData?.me?.type !== 4;
  return (
    <StyleSheetManager injectFirst>
      <ConfigProvider locale={locales(defaultClass)}>
        <Helmet>
          <meta charSet="utf-8" />
          <title>{getAppTitle(subdomain)}</title>
          <link rel="icon" href={getFavoriteIcon(subdomain)} />
          <link rel="apple-touch-icon" href={getFavoriteIcon(subdomain)} />
        </Helmet>
        <div className={defaultClass}>
          <MaintenanceNotice />
          <Suspense
            fallback={
              <div id="maincontentloader" className="app-loading">
                <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                  <Spin size="large" tip="Loading..." />
                </span>
              </div>
            }
          >
            {isEngEduRestricted ? (
              <EngEduNotice />
            ) : (
            <Switch>
              {userMeData?.me?.campus?.fc_company_idx===1052&&userMeData?.me?.type !== 4?<AirPlusLayout isDashboard={true} hideMenu={true} exact path="/" component={AirPlusDashBoard} />:<DefaultLayout exact path="/" component={DashBoard} />}
              <PublicLayout exact path="/bookr/redirect/:user_idx/:bookId/:assigned_idx/:path" component={BookrRedirect} />
              <PublicLayout exact path="/oauth/app" component={BlankAppPage} />
              <PublicLayout exact path="/privacy" component={Privacy} />
              <PublicLayout exact path="/policy" component={Policy} />
              <LoginLayout exact path="/login" component={Login2} />
              <PublicLayout exact path="/order-check/:idx" component={OrderCheck} />
              <PublicLayout exact path="/level-test" component={LevelTest} />
              <PublicLayout exact path="/leveltest" component={LevelTestNew} />
              <PublicLayout exact path="/level-test-main" component={LevelTestMain} />
              <PublicLayout exact path="/MetaExam/MetaUserInfo" component={MetaUserInfo} />
              <PublicLayout exact path="/meta-user-info" component={UserInfoNew} />
              <PublicLayout exact path="/meta-login" component={MetaLogin} />
              <PublicLayout exact path="/MetaExam/Survey" component={Survey} />
              <PublicLayout exact path="/MetaExam/MetaExamType" component={MetaExamType} />
              <PublicLayout exact path="/MetaExam/ReadingInfo" component={ReadingInfo} />
              <PublicLayout exact path="/MetaExam/ReadingTest" component={ReadingTest} />
              <PublicLayout exact path="/MetaExam/MetaFinished" component={MetaFinished} />
              <PublicLayout exact path="/MetaExam/MetaSampleQuestion" component={MetaSampleQuestion} />
              <PublicLayout exact path="/MetaExam/ImageTest" component={ImageTest} />
              <PublicLayout exact path="/MetaExam/MetaReport" component={MetaReport} />
              <PublicLayout exact path="/MetaExam/MetaReportAI" component={MetaReportAI} />
              <PublicLayout exact path="/educo/level-test/report/:id" component={StudentReportLevelTestEduco} />
              <PublicLayout exact path="/educo/level-test/report-elementary/:id" component={StudentReportLevelTestEducoElementary} />
              <PublicLayout exact path="/joshua/level-test/report/:id" component={StudentReportLevelTestJoshua} />
              <PublicLayout exact path="/wenglish/level-test/report/:id" component={StudentReportLevelTestWEnglish} />
              <PublicLayout exact path="/MetaExam/MetaTestStart/:id" component={MetaTestStart} />
              <PublicLayout exact path="/MetaExam/InstructionalGuideMeta" component={InstructionalGuideMeta} />
              <PublicLayout exact path="/MetaExam/InstructionalGuideMeta/complete" component={InstructionalGuideMeta} />
              <PublicLayout exact path="/MetaExam/InstructionalMetaExamType" component={InstructionalMetaExamType} />
              <PublicLayout exact path="/MetaExam/VocaTestSample" component={VocaTestSample} />
              <PublicLayout exact path="/MetaExam/ReadingTestSample" component={ReadingTestSample} />
              <PublicLayout exact path="/MetaExam/ListeningTestSample" component={ListeningTestSample} />
              <ApplicationFormLayout exact path="/placement-test-app" component={EvineLevelTestAppForm} />
              <ApplicationFormLayout exact path="/placement-test-register" component={PturnLevelTestAppForm} />
              <ApplicationFormLayout exact path="/application" component={LevelTestAppForm} />
              <PopupLayout exact path="/payletter/cancel" component={PayLetterCancel} />
              <PopupLayout exact path="/online-study-result/:user_idx" component={OnlineStudyResult} />
              <PopupLayout exact path="/elibrary-study-result/:user_idx" component={ElibraryStudyResult} />
              <PublicLayout exact path="/cmaker" component={Cmaker} />
              <DefaultLayout exact path="/faq" component={FAQ} />
              <DefaultLayout exact path="/faqevine" component={FAQEvine} />
              <LevelUpLayout2 exact path="/level-up2" component={LevelUpHome2} removeMargin={true} isHeaderStatic={true} hideMenu={true} />
              <LevelUpLayout3 exact path="/level-up" component={LevelUpHome3} removeMargin={true} isHeaderStatic={true} hideMenu={true} />
              <LevelUpLayout2
                exact
                path="/level-up2/search"
                component={LevelUpSearch2}
                removeMargin={true}
                isHeaderStatic={true}
                hideMenu={true}
              />
              <LevelUpLayout3
                exact
                path="/level-up/search"
                component={LevelUpSearch3}
                removeMargin={true}
                isHeaderStatic={true}
                hideMenu={true}
              />

              <LevelUpLayout3
                exact
                path="/level-up/results/reportandwpm"
                component={LevelUpReportandWPMResultPage}
                removeMargin={true}
                isHeaderStatic={false}
              />

              <LevelUpLayout3 exact path="/level-up/results/rubric/:id" component={RubricPage} removeMargin={true} isHeaderStatic={false} />
              <LevelUpLayout3 exact path="/level-up/bookreport" component={AiEssayCorrection} removeMargin={true} isHeaderStatic={false} />
              <LevelUpLayout2
                exact
                path="/level-up2/portfolio"
                component={LevelUpPortfolio2}
                removeMargin={true}
                isHeaderStatic={true}
                hideMenu={true}
              />
              <LevelUpLayout3
                exact
                path="/level-up/favorite"
                component={LevelUpFavorite3}
                removeMargin={true}
                isHeaderStatic={true}
                hideMenu={true}
              />
              <LevelUpLayout3
                exact
                path="/level-up/portfolio"
                component={LevelUpPortfolio3}
                removeMargin={true}
                isHeaderStatic={true}
                hideMenu={true}
              />
              <LevelUpLayout2
                exact
                path="/level-up2/inprogress"
                component={LevelUpInProgress2}
                removeMargin={true}
                isHeaderStatic={true}
                hideMenu={true}
              />
              <LevelUpLayout3
                exact
                path="/level-up/inprogress"
                component={LevelUpInProgress3}
                removeMargin={true}
                isHeaderStatic={true}
                hideMenu={true}
              />
              <LevelUpLayout2
                exact
                path="/level-up2/favorite"
                component={LevelUpFavorite2}
                removeMargin={true}
                isHeaderStatic={true}
                hideMenu={true}
              />
              <LevelUpLayout2
                exact
                path="/level-up2/calendar"
                component={LevelUpPointsAndCalendarPage2}
                removeMargin={true}
                isHeaderStatic={true}
                hideMenu={true}
              />
              <LevelUpLayout3
                exact
                path="/level-up/calendar"
                component={LevelUpPointsAndCalendarPage3}
                removeMargin={true}
                isHeaderStatic={true}
                hideMenu={true}
              />

              <LevelUpLayout3 exact path="/level-up/library" component={LevelUpLibrary3} removeMargin={true} isHeaderStatic={true} />
              <LevelUpLayout exact path="/level-up/wpm" component={LevelUpWPM} removeMargin={true} isHeaderStatic={true} />
              <LevelUpLayout exact path="/level-up/wpmresult" component={LevelUpWPMResult} removeMargin={true} isHeaderStatic={true} />

              <LevelUpLayout3 exact path="/level-up/:idx/matchinggame" component={CardMatchingGame} removeMargin={true} />
              <LevelUpLayout3 exact path="/level-up/:idx/crosspuzzlegame" component={CrossPuzzleGame} removeMargin={true} />
              <LevelUpLayout3 exact path="/level-up/:idx/wordfallgame" component={WordFallGame} removeMargin={true} />
              <LevelUpLayout3 exact path="/level-up/reader/:id" component={LevelUpReader} removeMargin={true} />
              <LevelUpLayout3 exact path="/level-up/epub-reader/:id" component={EpubReader} removeMargin={true} />

              <PublicLayout exact path="/testresult/:user_lesson_idx" component={JoshuaTestResult} />
              <LevelUpLayout3 exact path="/level-up/study/:idx" removeMargin={true} component={LevelUpBookStudyPage} />
              <LevelUpLayout3 exact path="/level-up/:idx/voca/card" removeMargin={true} component={LevelUpVocaCard} />
              <LevelUpLayout3 exact path="/level-up/:idx/voca/type" removeMargin={true} component={LevelUpVocaType} />
              <LevelUpLayout3 exact path="/level-up/:idx/voca/match" removeMargin={true} component={LevelUpVocaMatch} />
              <LevelUpLayout3 exact path="/level-up/:idx/voca/quiz" removeMargin={true} component={LevelUpVocaQuiz} />
              <LevelUpLayout3 exact path="/level-up/:idx/sentence/card" removeMargin={true} component={LevelUpSentenceCard} />
              <LevelUpLayout3 exact path="/level-up/:idx/sentence/repeat" removeMargin={true} component={LevelUpSentenceRepeat} />
              <LevelUpLayout3 exact path="/level-up/:idx/sentence/dnd" removeMargin={true} component={LevelUpSentenceDnD} />
              <LevelUpLayout3 exact path="/level-up/:idx/sentence/dictation" removeMargin={true} component={LevelUpSentenceDictation} />
              <LevelUpLayout3 exact path="/level-up/:idx/quiz1" removeMargin={true} component={LevelUpQuiz1} />
              <LevelUpLayout3 exact path="/level-up/:idx/quiz2" removeMargin={true} component={LevelUpQuiz2} />
              <LevelUpLayout3 exact path="/level-up/:idx/quiz3" removeMargin={true} component={LevelUpQuiz3} />
              <LevelUpLayout3 exact path="/level-up/report" removeMargin={true} component={LevelUpManage} />
              <PublicLayout
                exact
                path="/level-up/report/generate/:id"
                isHeaderStatic={false}
                removeMargin={true}
                component={LevelUpReportGenerate}
              />
              <LevelUpLayout3 exact path="/level-up/report/:type" removeMargin={true} component={LevelUpManage} />
              <LevelUpLayout3 exact path="/level-up/report/details-by-date/:idx" removeMargin={true} component={LevelUpDetailsByDate} />
              <LevelUpLayout3
                exact
                path="/level-up/report/study/:idx"
                component={LevelUpBookReportStudy}
                removeMargin={true}
                isHeaderStatic={true}
              />
              <LevelUpLayout3 exact path="/level-up/recording/:id" removeMargin={true} component={LevelUpTool} />

              {/* air+ Library Routes */}
              <AirPlusLayout exact path="/air-plus" component={AirPlusHome} removeMargin={true} isHeaderStatic={true} />
              <AirPlusLayout exact path="/air-plus/library" component={AirPlusLibrary} removeMargin={true} isHeaderStatic={true} />
              <AirPlusLayout exact path="/air-plus/search" component={AirPlusSearch} removeMargin={true} isHeaderStatic={true} hideSearch={true} />
              <AirPlusLayout exact path="/air-plus/favorite" component={AirPlusPortfolio} removeMargin={true} isHeaderStatic={true} />
              <AirPlusLayout exact path="/air-plus/inprogress" component={AirPlusPortfolio} removeMargin={true} isHeaderStatic={true} />
              <AirPlusLayout exact path="/air-plus/portfolio" component={AirPlusPortfolio} removeMargin={true} isHeaderStatic={true} />
              <AirPlusLayout exact path="/air-plus/book-reports" component={AirPlusBookReportList} removeMargin={true} isHeaderStatic={true} />
              <AirPlusLayout
                exact
                path="/air-plus/results/assignresult"
                component={AirPlusAssignResult}
                removeMargin={true}
                hideMenu={true}
                isHeaderStatic={true}
              />
              <PublicLayout
                exact
                path="/air-plus/bookreport/generate/:id"
                isHeaderStatic={false}
                removeMargin={true}
                component={AirPlusBookReportGenerate}
              />
              <AirPlusLayout exact path="/air-plus/calendar" component={AirPlusPortfolio} removeMargin={true} isHeaderStatic={true} />
              <AirPlusLayout exact path="/air-plus/report/details-by-date/:idx" component={AirPlusReportDetailsByDate} removeMargin={true} isHeaderStatic={true} />
              <AirPlusLayout exact path="/air-plus/results/rubric/:id" component={AirPlusReportRubric} removeMargin={true} isHeaderStatic={true} />
              <AirPlusLayout exact path="/air-plus/mypage" component={AirPlusMyPage} removeMargin={true} isHeaderStatic={true} />
              <AirPlusLayout exact path="/air-plus/level-test" component={AirPlusLevelTest} removeMargin={true} isHeaderStatic={true} />
              <AirPlusLayout exact path="/air-plus/level-test/take" component={LevelUpLevelTest} removeMargin={true} hideMenu={true} isHeaderStatic={false} />
              <AirPlusLayout exact path="/air-plus/description/:idx" component={AirPlusBookDescriptionPage} removeMargin={true} hideMenu={true} />
              <AirPlusLayout exact path="/air-plus/study/:idx" removeMargin={true} hideMenu={true} component={AirPlusBookStudyPage} />
              <AirPlusLayout exact path="/air-plus/study/:idx/:assigned_idx" removeMargin={true} hideMenu={true} component={AirPlusBookStudyPage} />
              <AirPlusLayout exact path="/air-plus/recording/:id" removeMargin={true} hideMenu={true} component={AirPlusRecording} />
              <AirPlusLayout exact path="/air-plus/recording/:id/:assigned_idx" removeMargin={true} hideMenu={true} component={AirPlusRecording} />
              <AirPlusLayout exact path="/air-plus/fluency/:id" removeMargin={true} hideMenu={true} component={AirPlusRecording} />
              <AirPlusLayout exact path="/air-plus/fluency/:id/:assigned_idx" removeMargin={true} hideMenu={true} component={AirPlusRecording} />
              <AirPlusLayout exact path="/air-plus/ai-discussion/:id" removeMargin={true} hideMenu={true} component={AirPlusFluency} />
              <AirPlusLayout exact path="/air-plus/ai-discussion/:id/:assigned_idx" removeMargin={true} hideMenu={true} component={AirPlusFluency} />
              <AirPlusLayout exact path="/air-plus/reader/:id" component={AirPlusReader} removeMargin={true} hideMenu={true} />
              <AirPlusLayout exact path="/air-plus/reader/:id/:assigned_idx" component={AirPlusReader} removeMargin={true} hideMenu={true} />
              <AirPlusLayout exact path="/air-plus/epub-reader/:id" component={AirPlusEpubReader} removeMargin={true} hideMenu={true} />
              <AirPlusLayout exact path="/air-plus/epub-reader/:id/:assigned_idx" component={AirPlusEpubReader} removeMargin={true} hideMenu={true} />
              <AirPlusLayout exact path="/air-plus/:idx/matchinggame" component={AirPlusCardMatchGame} removeMargin={true} hideMenu={true} />
              <AirPlusLayout exact path="/air-plus/:idx/matchinggame/:assigned_idx" component={AirPlusCardMatchGame} removeMargin={true} hideMenu={true} />
              <AirPlusLayout exact path="/air-plus/:idx/memorygame" component={AirPlusWordMatchGame} removeMargin={true} hideMenu={true} />
              <AirPlusLayout exact path="/air-plus/:idx/memorygame/:assigned_idx" component={AirPlusWordMatchGame} removeMargin={true} hideMenu={true} />
              <AirPlusLayout exact path="/air-plus/:idx/crosspuzzlegame" component={AirPlusCrossPuzzleGame} removeMargin={true} hideMenu={true} />
              <AirPlusLayout exact path="/air-plus/:idx/crosspuzzlegame/:assigned_idx" component={AirPlusCrossPuzzleGame} removeMargin={true} hideMenu={true} />
              <AirPlusLayout exact path="/air-plus/:idx/wordfallgame" component={AirPlusWordFallGame} removeMargin={true} hideMenu={true} />
              <AirPlusLayout exact path="/air-plus/:idx/wordfallgame/:assigned_idx" component={AirPlusWordFallGame} removeMargin={true} hideMenu={true} />
              <AirPlusLayout exact path="/air-plus/:idx/voca/card" removeMargin={true} hideMenu={true} component={AirPlusVocaCard} />
              <AirPlusLayout exact path="/air-plus/:idx/voca/card/:assigned_idx" removeMargin={true} hideMenu={true} component={AirPlusVocaCard} />
              <AirPlusLayout exact path="/air-plus/:idx/voca/type" removeMargin={true} hideMenu={true} component={AirPlusVocaType} />
              <AirPlusLayout exact path="/air-plus/:idx/voca/type/:assigned_idx" removeMargin={true} hideMenu={true} component={AirPlusVocaType} />
              <AirPlusLayout exact path="/air-plus/:idx/voca/match" removeMargin={true} hideMenu={true} component={AirPlusVocaMatch} />
              <AirPlusLayout exact path="/air-plus/:idx/voca/match/:assigned_idx" removeMargin={true} hideMenu={true} component={AirPlusVocaMatch} />
              <AirPlusLayout exact path="/air-plus/:idx/voca/quiz" removeMargin={true} hideMenu={true} component={AirPlusVocaQuiz} />
              <AirPlusLayout exact path="/air-plus/:idx/voca/quiz/:assigned_idx" removeMargin={true} hideMenu={true} component={AirPlusVocaQuiz} />
              <AirPlusLayout exact path="/air-plus/:idx/sentence/card" removeMargin={true} hideMenu={true} component={LevelUpSentenceCard} />
              <AirPlusLayout exact path="/air-plus/:idx/sentence/repeat" removeMargin={true} hideMenu={true} component={LevelUpSentenceRepeat} />
              <AirPlusLayout exact path="/air-plus/:idx/sentence/dnd" removeMargin={true} hideMenu={true} component={LevelUpSentenceDnD} />
              <AirPlusLayout exact path="/air-plus/:idx/sentence/dictation" removeMargin={true} hideMenu={true} component={LevelUpSentenceDictation} />
              <AirPlusLayout exact path="/air-plus/:idx/quiz1" removeMargin={true} hideMenu={true} component={LevelUpQuiz1} />
              <AirPlusLayout exact path="/air-plus/:idx/quiz2" removeMargin={true} hideMenu={true} component={LevelUpQuiz2} />
              <AirPlusLayout exact path="/air-plus/:idx/quiz3" removeMargin={true} hideMenu={true} component={LevelUpQuiz3} />
              <AirPlusLayout exact path="/air-plus/bookreport" component={AirPlusBookReport} removeMargin={true} hideMenu={true} isHeaderStatic={false} />
              <DDSCLayout exact path="/ddsc" component={DDSC} />
              <DDSCLayout exact path="/ddsc/roadmap" component={DDSCRoadmap} />
              <DDSCLayout exact path="/ddsc/voca" component={DDSCVoca} />
              <DDSCLayout exact path="/ddsc/quiz" component={DDSCQuiz} />
              <DDSCLayout exact path="/ddsc/tutor" component={DDSCTutor} />
              <DDSCLayout exact path="/ddsc/battle" component={DDSCBattle} />
              <ToolLayout exact path="/bookr/:type/:id" component={BookrTool} />
              <ToolLayout exact path="/external-tool/:type" component={ExternalTool} />

              <PhonicsLayout exact isHeaderStatic={true} path="/phm/:level/unit/:unit" component={PhonicsMonsterStageList} />
              <PhonicsLayout exact isHeaderStatic={true} path="/phm/:level/unit/:unit/stage/:stage" component={PhonicsMonsterStage} />
              <PhonicsLayout exact isHeaderStatic={true} path="/pm/:level/unit/:unit" component={PhonicsMonsterStageList} />
              <PhonicsLayout exact isHeaderStatic={true} path="/pm/:level/unit/:unit/stage/:stage" component={PhonicsMonsterStage} />

              {/* <AICoachLayout exact path="/aicoach/report/details-by-date/:idx" removeMargin={true} component={LevelUpDetailsByDate} /> */}
              <AICoachNewLayout exact path="/aicoach/writing/:idx" removeMargin={true} component={AiWritingCoach} />
              <AICoachNewLayout exact path="/aiwriting/writing/:idx" removeMargin={true} component={AIWritingCoachWhale} />
              <AITalkingTutorLayout exact path="/ai-homework/:user_idx(\\d+)/result" removeMargin={true} component={StudentAIHomeworkResult} />
              <AITalkingTutorLayout exact path="/ai-homework/:user_idx(\\d+)" removeMargin={true} component={StudentAIHomeworkAssignment} />
              <AITalkingTutorLayout exact path="/ai-talking-tutor/speaking/:idx" removeMargin={true} component={AiTalkingStudy} />
              <AITalkingTutorLayout exact path="/ai-talking-tutor/speaking-real/:idx" removeMargin={true} component={AiTalkingStudyReal} />
              <PublicLayout
                exact
                path="/aicoach/report/generate/:id"
                isHeaderStatic={false}
                removeMargin={true}
                component={AICoachReportGenerate}
              />

              <PublicLayout
                exact
                path="/aiwriting/report/generate/:id"
                isHeaderStatic={false}
                removeMargin={true}
                component={AIWritingReportGenerate}
              />
              <PublicLayout
                exact
                path="/ai-talking-tutor/report/generate/:id"
                isHeaderStatic={false}
                removeMargin={true}
                component={AITalkingTutorReportGenerate}
              />
              <PublicLayout exact path="/monthly-report/:type" isHeaderStatic={false} removeMargin={true} component={MonthlyReport} />
              <AICoachLayout
                exact
                path="/aicoach/results/rubric/:id"
                component={RubricAiCoachPage}
                removeMargin={true}
                isHeaderStatic={false}
              />
              <AICoachLayout
                exact
                path="/aiwriting/results/rubric/:id"
                component={RubricAIWritingPage}
                removeMargin={true}
                isHeaderStatic={false}
              />
              <DefaultLayout exact path="/star-words/portfolio" component={StarWordsPortfolio} />
              <PublicLayout exact path="/vocatool" component={VocaTool} />
              <PublicLayout exact path="/vocaQuizFrame" component={VocaQuizFrame} />
              <PublicLayout exact path="/vocaFullWidth" component={VocaFullWidth} />
              {/*  */}
              <PublicLayout exact path="/VocaFlashCardTyping" component={VocaFlashCardTyping} />
              <PublicLayout exact path="/VocaFlashcard" component={VocaFlashcard} />
              <PublicLayout exact path="/VocaUnscramble" component={VocaUnscramble} />
              <PublicLayout exact path="/vocacheck" component={VocaCheck} />
              <PublicLayout exact path="/vocadictation" component={VocaDictation} />
              <PublicLayout exact path="/no-access" component={NoAccess} />
              {/*  */}
              {token && userMeData?.me?.type === 5 ? (
                <Administrator />
              ) : userMeData?.me?.type === 4 ? (
                <HeadManager />
              ) : userMeData?.me?.type === 3 ? (
                <BranchManager />
              ) : userMeData?.me?.type === 2 || userMeData?.me?.type === 6 ? (
                <CampusManager />
              ) : userMeData?.me?.type === 1 ? (
                <Teacher />
              ) : (
                <Student />
              )}
            </Switch>
            )}
          </Suspense>
        </div>
      </ConfigProvider>
    </StyleSheetManager>
  );
}

export default App;
